import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Globe, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const FLAG_LABEL = {
  intl_signal_conflict: 'Signals disagree',
  usd_shown_to_india_device: 'USD shown on Indian-clock device',
};

// V25 C6 — resolved country / currency, device signals, app events + manual override.
export const CountrySignalsCard = ({ userId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/api/admin/users/${userId}/country-signals`)
    .then((res) => setData(res.data)).catch(() => {});

  useEffect(() => { if (userId) load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setOverride = async (override) => {
    setBusy(true);
    try {
      const res = await api.post(`/api/admin/users/${userId}/country-override`, {
        override, reason: override ? `Marked ${override === 'IN' ? 'India' : 'international'} from LMS admin` : 'Cleared override',
      });
      toast.success(`Now ${res.data.country} · ${res.data.currency} — the app re-quotes on next open`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Override failed');
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const r = data.resolved || {};
  const dev = data.device_signals || {};
  const ov = data.override?.value;
  const intl = !!r.is_international;

  return (
    <div className="p-4 rounded-xl border" data-testid="country-signals-card">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" />Country &amp; pricing</h4>
        <button type="button" onClick={load} className="text-muted-foreground hover:text-foreground" data-testid="country-signals-refresh" aria-label="Refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <Badge variant="outline" className={intl ? 'bg-sky-50 text-sky-800 border-sky-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'} data-testid="country-resolved-badge">
          {r.country} · {r.currency} · {intl ? 'International' : 'India'}
        </Badge>
        <span className="text-[11px] text-muted-foreground" data-testid="country-resolved-reason">
          via {String(r.reason || '').replace(/_/g, ' ')} · confidence {r.confidence}
        </span>
      </div>
      {(r.flags || []).length > 0 && (
        <div className="mt-1.5 flex gap-1 flex-wrap" data-testid="country-flags">
          {r.flags.map((f) => <Badge key={f} variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">{FLAG_LABEL[f] || f}</Badge>)}
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground" data-testid="country-device-signals">
        <span className="col-span-2" data-testid="user-timezone">Timezone: <b className="text-foreground">{data.timezone?.name || '—'}</b>
          {data.timezone?.utc_offset ? ` (UTC${data.timezone.utc_offset})` : ''}
          {data.timezone?.source ? ` · via ${String(data.timezone.source).replace(/_/g, ' ')}` : ''}</span>
        <span>Clock: <b className="text-foreground">{dev.device_timezone || '—'}</b></span>
        <span>OS region: <b className="text-foreground">{dev.device_region || '—'}</b></span>
        <span>Locale: <b className="text-foreground">{dev.device_locale || '—'}</b></span>
        <span>IP / card / phone: <b className="text-foreground">{data.signals?.ip || '—'} / {data.signals?.card || '—'} / {data.signals?.phone || '—'}</b></span>
      </div>
      {r.votes && (r.votes._in !== undefined || r.votes._intl !== undefined) && (
        <div className="mt-2 rounded-md bg-muted/40 px-2.5 py-1.5 text-[11px]" data-testid="country-vote-tally">
          <span className="text-muted-foreground">Weighted vote — </span>
          <b className={r.is_international ? 'text-foreground' : 'text-emerald-700'}>India {r.votes._in ?? 0}</b>
          <span className="text-muted-foreground"> vs </span>
          <b className={r.is_international ? 'text-emerald-700' : 'text-foreground'}>International {r.votes._intl ?? 0}</b>
          <span className="text-muted-foreground">
            {' '}({Object.entries(r.votes).filter(([k]) => !k.startsWith('_'))
              .map(([k, v]) => `${k}=${v}`).join(', ') || 'no signals'})
          </span>
          {r.learner_claim_ignored && (
            <span className="ml-1 text-amber-800">· learner claimed otherwise — ignored, review raised</span>
          )}
        </div>
      )}
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        <span className="text-[11px] text-muted-foreground mr-1">Override:</span>
        <Button size="sm" variant={!ov ? 'default' : 'outline'} className="h-7 text-xs" disabled={busy} onClick={() => setOverride(null)} data-testid="country-override-auto">Auto</Button>
        <Button size="sm" variant={ov === 'IN' ? 'default' : 'outline'} className="h-7 text-xs" disabled={busy} onClick={() => setOverride('IN')} data-testid="country-override-india">Mark as India</Button>
        <Button size="sm" variant={ov === 'INTERNATIONAL' ? 'default' : 'outline'} className="h-7 text-xs" disabled={busy} onClick={() => setOverride('international')} data-testid="country-override-intl">Mark as international</Button>
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      </div>
      {ov && data.override?.by && (
        <p className="mt-1 text-[11px] text-muted-foreground" data-testid="country-override-meta">
          Set by {data.override.by}{data.override.reason ? ` — ${data.override.reason}` : ''}
        </p>
      )}
      {(data.events || []).length > 0 && (
        <details className="mt-2 text-[11px]">
          <summary className="cursor-pointer text-muted-foreground">App country events ({data.events.length})</summary>
          <ul className="mt-1 space-y-0.5" data-testid="country-events">
            {data.events.slice(0, 8).map((e) => (
              <li key={e.id || e._id} className="text-muted-foreground">
                <span className="text-foreground">{e.event_name}</span> · {e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
